import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import userSelectors from '@redux/user/selectors';

import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import { useGetProductFullView } from 'hooks';

import { checkIsDollarProduct } from 'pages/qa-spec/views/qa-spec-detail/utils';

import * as qasSpecServices from 'services/qas-require-field';
export const requiredFieldsMap = {
  //* header
  'qaSpecification.category1Name': 'qaSpecification.category1',
  'qaSpecification.category2Name': 'qaSpecification.category2',
  'qaSpecification.category3Name': 'qaSpecification.category3',
  'qaSpecification.category4Name': 'qaSpecification.category4',
  'eCommerce.smartLabelTemplate': 'qaSpecification.smartLabelTemplate',
  //* product spec tab
  'tdcProperties.glutenFreeYN':
    'qaSpecification.qaSpecProductSpecification.glutenFreeClaim',
  'qaSpecification.safeHandlingInstructions':
    'qaSpecification.qaSpecProductSpecification.safeHandlingInstructions',
  targetMarket: 'qaSpecification.qaSpecProductSpecification.targetMarket',
  'placeOfItemActivity.placeOfProductActivity.countryOfOrigin.countryCode':
    'qaSpecification.qaSpecProductSpecification.countryOfOrigin.countryCode',
  'placeOfItemActivity.placeOfProductActivity.countryOfOrigin.countrySubdivisionCode':
    'qaSpecification.qaSpecProductSpecification.countryOfOrigin.countrySubdivisionCode',
  'tdcProperties.unit_MinimumShelfLife':
    'qaSpecification.qaSpecProductSpecification.minimumShelfLife',
  'dProperties.bpSKULabelling.leftLanguage.qualityGuarantee':
    'qaSpecification.qaSpecProductSpecification.qualityGuarantee',
  'tdcProperties.unit_MaximumShelfLife':
    'qaSpecification.qaSpecProductSpecification.maximumShelfLife',
  'tdcProperties.nonGmoGmoStatement':
    'qaSpecification.qaSpecProductSpecification.nonGmoStatement',
  'tdcProperties.case_TemperatureRangeHigh':
    'qaSpecification.qaSpecProductSpecification.caseTemperatureRangeHigh',
  'tdcProperties.case_TemperatureRangeLow':
    'qaSpecification.qaSpecProductSpecification.caseTemperatureRangeLow',
  'tdcProperties.case_ShippingTemperatureRangeHigh':
    'qaSpecification.qaSpecProductSpecification.caseShippingTemperatureHigh',
  'tdcProperties.case_ShippingTemperatureRangeLow':
    'qaSpecification.qaSpecProductSpecification.caseShippingTemperatureLow',
  'nonfoodIngredient.nonfoodIngredientStatement.value':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.value',
  'nonfoodIngredient.nonfoodIngredientStatement.languageCode':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.languageCode',
  'nonfoodIngredient.nonfoodIngredientStatement.codeListVersion':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.codeListVersion',
  'nonfoodIngredient.nonfoodIngredientStatement.formattingPattern':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.formattingPattern',
  'nonfoodIngredient.nonfoodIngredientStatement.sequenceNumber':
    'qaSpecification.qaSpecProductSpecification.nonfoodIngredientStatement.sequenceNumber',
  'certification.kosherCertification':
    'qaSpecification.qaSpecProductSpecification.kosherCertifier',
  'certification.organicCertifier':
    'qaSpecification.qaSpecProductSpecification.organicCertifier',
  'certification.halalCertifier':
    'qaSpecification.qaSpecProductSpecification.halalCertifier',
  'certification.nonGMOCertifier':
    'qaSpecification.qaSpecProductSpecification.nonGMOCertifier',
  'certification.iSOCertifier':
    'qaSpecification.qaSpecProductSpecification.iSOCertifier',
  'certification.bRCCertifier':
    'qaSpecification.qaSpecProductSpecification.bRCCertifier',
  'certification.iFSCertifier':
    'qaSpecification.qaSpecProductSpecification.iFSCertifier',
  //* labeling tab
  'consumerInstructions.consumerInstructions.consumerStorageInstructions.value':
    'qaSpecification.qaSpecPackaging.storageInstructions',
  'tradeItemHandling.tradeItemHandlingInformation.handlingInstructionsDescription.value':
    'qaSpecification.qaSpecPackaging.handlingInstructions',
  'consumerInstructions.consumerInstructions.consumerUsageInstructions.value':
    'qaSpecification.qaSpecPackaging.usage',
  'sustainability.sustainabilityInformation.totalRecyclableContentPercentage':
    'qaSpecification.qaSpecPackaging.totalRecyclableContentPercentage',
  'sustainability.sustainabilityInformation.postConsumerRecycledContentPercentage':
    'qaSpecification.qaSpecPackaging.postConsumerRecycledContentPercentage',
  'packagingMarking.packagingMarking.consumerWarningInformation.consumerWarningDescription.value':
    'qaSpecification.qaSpecPackaging.warningStatement',
  productName: 'qaSpecification.qaSpecPackaging.productName',
  upc12: 'qaSpecification.qaSpecPackaging.upc12',
  primarySize: 'qaSpecification.qaSpecPackaging.size',
  primarySizeUOM: 'qaSpecification.qaSpecPackaging.uom',
  'dProperties.bpSKULabelling.leftLanguage.frontPanelSecondaryDescrip':
    'qaSpecification.qaSpecPackaging.secondaryDescriptor',
  'dProperties.bpSKULabelling.leftLanguage.tertiaryDescriptor':
    'qaSpecification.qaSpecPackaging.tertiaryDescriptor',
  'eCommerce.rivirMarketingDescription':
    'qaSpecification.qaSpecPackaging.romanceCopy',
  'foodAndBeveragePreparationServing.preparationServing.recipe.value':
    'qaSpecification.qaSpecPackaging.recipe',
  //* packaging (labeling) update after EPR packaging US 297527
  'packagingMarking.packagingMarking.isTradeItemMarkedAsRecyclable':
    'qaSpecification.qaSpecPackaging.recyclable',
  'packagingMarking.packagingMarking.packagingMarkedRecyclableScheme':
    'qaSpecification.qaSpecPackaging.recyclabletypes',
  'tdcProperties.containerType':
    'qaSpecification.qaSpecPackaging.packagingtype',
  'tdcProperties.containerAndMaterialTypes':
    'qaSpecification.qaSpecPackaging.packagingmaterial',
  //* ingredient tab
  'foodAndBeverageIngredient.ingredientStatement.value':
    'qaSpecification.qaSpecIngredients.ingredientStatement',
  'dProperties.ingredientsProductClaims':
    'qaSpecification.qaSpecIngredients.ingredientClaims',
  //* fresh meat
  'foodAndBeverageIngredient.foodAndBeverageIngredient.ingredientFarmingProcessing.irradiatedCode':
    'qaSpecification.qaSpecFreshMeat.irradiated',
  //* fresh produce
  'foodAndBeveragePreparationServing.preparationServing.preparationInstructions.value':
    'qaSpecification.qaSpecFreshProduce.cookingInstructions',
  //* fresh sea food
  'organismClassification.organismClassification.genus':
    'qaSpecification.qaSpecFreshSeaFood.genus',
  'organismClassification.organismClassification.species':
    'qaSpecification.qaSpecFreshSeaFood.species',
  //* component
  'dProperties.componentsProductClaims':
    'qaSpecification.bpComponents.productClaims',
  //* Pet food
  'tdcProperties.productWarningStatement':
    'qaSpecification.qaSpecPetFood.warnings',
  //* Fresh Deli
  'qaSpecification.typeOfCutOrTrimSpec':
    'qaSpecification.qaSpecFreshMeat.typeOfCutOrTrimSpec',
  //* Contact
  //* fact panel
  'qaSpecification.factsPanel.supplementLabel.servingSize.servingSizeDescription':
    'qaSpecification.factsPanel.supplementLabel.servingSizeDescription',
  'qaSpecification.factsPanel.drugFactsLabel.activeIngredients.dosageUnit':
    'qaSpecification.factsPanel.drugFactsLabel.dosageUnit',
  //* EPR packaging - master case
  'packagingInformation.packaging.numberOfUnitInShippingContainer.shippingContainerQuantity.value':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.case',

  'packagingInformation.packaging.packagingTypeCode':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.packagingtype',

  'packagingInformation.packaging.packagingMaterial.packagingMaterialTypeCode':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.packagingmaterial',

  'packagingInformation.packaging.packagingFunctionCode':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.packagingFunctionCode',

  'packagingInformation.packaging.packagingTypeDescription.value':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.materialdescription',

  'packagingInformation.packaging.packagingWeight.value':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.materialweight',

  'packagingInformation.packaging.packagingWeight.measurementUnitCode':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.materialuom',

  'packagingInformation.packaging.packagingMaterial.isPackagingMaterialRecoverable':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.recyclable',

  'packagingInformation.packaging.packagingRecyclingSchemeCode':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.recyclabletype',

  'packagingInformation.packaging.packagingRecyclingProcessTypeCode':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.packagingRecyclingProcessTypeCode',

  'packagingSustainability.individualPackagingComponentLevel.packagingRecycledContent.packagingRecycledContentRatio.total':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.totalrecycledcontentpercentage',

  'packagingSustainability.individualPackagingComponentLevel.packagingRecycledContent.packagingRecycledContentRatio.pre':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.preconsumerrecycledcontentpercentage',

  'packagingSustainability.individualPackagingComponentLevel.packagingRecycledContent.packagingRecycledContentRatio.post':
    'qaspecification.qaspeceprpackaging.mastercasecomponents.postconsumerrecycledcontentpercentage',

  //* EPR packaging - retailer unit
  'packagingInformation.packaging.numberOfUnitInShippingContainer.shippingContainerQuantity.value.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.unit',

  'packagingInformation.packaging.packagingTypeCode.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.packagingtype',

  'packagingInformation.packaging.packagingMaterial.packagingMaterialTypeCode.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.packagingmaterial',

  'packagingInformation.packaging.packagingFunctionCode.unit':
    'qaspecification.qaspeceprpackaging.retailerUnitComponents.packagingFunctionCode',

  'packagingInformation.packaging.packagingTypeDescription.value.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.materialdescription',

  'packagingInformation.packaging.packagingWeight.value.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.materialweight',

  'packagingInformation.packaging.packagingWeight.measurementUnitCode.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.materialuom',

  'packagingInformation.packaging.packagingMaterial.isPackagingMaterialRecoverable.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.recyclable',

  'packagingInformation.packaging.packagingRecyclingSchemeCode.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.recyclabletype',

  'packagingInformation.packaging.packagingRecyclingProcessTypeCode.unit':
    'qaspecification.qaspeceprpackaging.retailerUnitComponents.packagingRecyclingProcessTypeCode',

  'packagingSustainability.individualPackagingComponentLevel.packagingRecycledContent.packagingRecycledContentRatio.total.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.totalrecycledcontentpercentage',

  'packagingSustainability.individualPackagingComponentLevel.packagingRecycledContent.packagingRecycledContentRatio.pre.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.preconsumerrecycledcontentpercentage',

  'packagingSustainability.individualPackagingComponentLevel.packagingRecycledContent.packagingRecycledContentRatio.post.unit':
    'qaspecification.qaspeceprpackaging.retailerunitcomponents.postconsumerrecycledcontentpercentage',
};

export const getGetQaSpecRequiredFieldsKeys = (params, userId) => {
  return ['get-qa-spec-required-fields', params, userId];
};

export const useGetQaSpecRequiredFields = (props) => {
  const { params, ...options } = props;
  const userInfo = useSelector(userSelectors.makeSelectUserInfo());

  const isDollarProduct = checkIsDollarProduct(params?.ownerId);

  const getGetQaSpecRequiredFields = async ({ queryKey }) => {
    const params = queryKey[1];

    const response =
      await qasSpecServices.getQaSpecRequiredFieldForProduct(params);

    if (!response.isSuccess || !response?.data?.requiredFields) return [];

    // map data
    const requiredFields = response.data.requiredFields
      .filter((field) => {
        //* filter out product contain field when product does not belong to dollar general

        //* if product  belongs to dollar general, accept all fields
        if (isDollarProduct) return true;

        //* if not, accept fields that is not product contain fields
        const isProductContainField = field.includes(
          'qaSpecification.qaSpecProductContains',
        );

        return !isProductContainField;
      })
      .map((field) => {
        //* some fields  in Product Spec tab have difference fieldName with required fields, format them to standard field full path for logic flow
        const formatted = requiredFieldsMap[field];
        return (formatted ? formatted : field).toLowerCase();
      });

    // attach contacts as a property
    requiredFields.requiredContactFields =
      response.data.requiredContactFields || [];

    return requiredFields;
  };

  return useQuery({
    queryKey: getGetQaSpecRequiredFieldsKeys(params, { userId: userInfo?.id }),
    queryFn: getGetQaSpecRequiredFields,
    ...options,
  });
};

export const useGetQaSpecRequiredFieldsInQaSpecFullView = (props) => {
  const { productId } = props;

  const { productFull } = useGetProductFullView({
    productId,
  });

  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productId,
    isEnabled: !!productId,
  });

  const productType = qaSpecDataHeader?.type ?? null;

  return useGetQaSpecRequiredFields({
    params: {
      ownerId: productFull?.ownerId,
      productType,
      productId: productId,
    },
    enabled: !!productFull && !!productType && !!productFull?.ownerId,
  });
};
